import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Settings from '../Settings';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import SchemaForm from '@afosto/app-components-react/SchemaForm';
import { highlightSubstring } from '@afosto/app-components-react/Autocomplete';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import translations from './translations';

const SENDER_PATH = 'afosto.tenant.rel.identity.v1.email.outgoing';

const NotificationSettings = ({ pathID }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['rel:identity:read']);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [senderSettings, setSenderSettings] = useState(null);
  const [formSchema, setFormSchema] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const hasReadPermissions = hasPermissions();

  const formatOptions = data =>
    data.reduce(
      (acc, result) => [
        ...acc,
        {
          ...result,
          value: `${result.prefix}@${result.fqdn}`,
        },
      ],
      [],
    );

  const getOptionLabel = option => option.value ?? option;

  const renderInboxSuggestion = (option, { inputValue = '' } = {}) => {
    const { label, value } = option;

    return (
      <ListItem component="div" dense disableGutters>
        <ListItemText
          primary={highlightSubstring(label, inputValue)}
          secondary={highlightSubstring(value, inputValue)}
        />
      </ListItem>
    );
  };

  const uiSchema = {
    name: {
      props: {
        label: intl.formatMessage(translations.editDrawer.nameField.label),
      },
    },
    email: {
      component: 'AsyncAutocomplete',
      props: {
        label: intl.formatMessage(translations.editDrawer.emailField.label),
        url: '/mes/inboxes',
        renderOption: renderInboxSuggestion,
        formatOptions,
        getOptionLabel,
        valueOnly: true,
      },
    },
  };

  const handleUpdateProfile = async fields => {
    try {
      const payload = {
        name: fields.name,
        email: fields.email,
      };

      const { data } = await profileApi.updateProfile(
        SENDER_PATH.replace('tenant', pathID),
        payload,
      );
      setSenderSettings(data);
      enqueueSnackbar(intl.formatMessage(translations.feedback.notificationSettingsUpdated), {
        variant: 'success',
      });
      setIsEditing(false);
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.notificationSettingsNotUpdated), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const handleFetchProfile = async () => {
      try {
        const { data } = await profileApi.getProfile(SENDER_PATH.replace('tenant', pathID));

        if (isMounted) {
          setSenderSettings(data);
        }
      } catch (error) {
        if (isMounted) {
          setSenderSettings(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const fetchProfileFormSchema = async () => {
      try {
        const { data } = await profileApi.getProfileDefinitions(
          SENDER_PATH.replace('tenant', pathID),
        );
        if (isMounted) {
          setFormSchema(data);
        }
      } catch (error) {
        if (isMounted) {
          setFormSchema({});
        }
      }
    };

    if (hasReadPermissions) {
      handleFetchProfile();
      fetchProfileFormSchema();
    }

    return () => {
      isMounted = false;
    };
  }, [pathID, hasReadPermissions]);

  if (!hasReadPermissions) {
    return (
      <Settings title={intl.formatMessage(translations.title)} isLoading={false}>
        <UnauthorizedComponent />
      </Settings>
    );
  }

  return (
    <>
      <Settings title={intl.formatMessage(translations.title)} isLoading={isLoading}>
        <List dense disablePadding>
          <ListItem>
            <ListItemText primary={senderSettings?.name} secondary={senderSettings?.email} />
            {hasPermissions(['rel:identity:write']) && (
              <Button onClick={() => setIsEditing(true)} variant="outlined" size="small">
                <FormattedMessage {...translations.actions.edit} />
              </Button>
            )}
          </ListItem>
        </List>
      </Settings>
      <FormDrawer
        isOpen={isEditing}
        title={intl.formatMessage(translations.editDrawer.title)}
        drawerWidth="420px"
        onClose={() => setIsEditing(false)}
      >
        {formSchema && (
          <SchemaForm
            schema={formSchema}
            uiSchema={uiSchema}
            values={{
              name: senderSettings?.name,
              email: senderSettings?.email,
            }}
            onSubmit={handleUpdateProfile}
            onCancel={() => setIsEditing(false)}
          />
        )}
      </FormDrawer>
    </>
  );
};

NotificationSettings.propTypes = {
  pathID: PropTypes.string,
};

NotificationSettings.defaultProps = {
  pathID: 'tenant',
};

export default NotificationSettings;
